import type {
    BillingCurrency,
    Cadence,
} from "@/types/generated/management-api-types";

// "€99" reads better than "€99.00" — but a price with cents still shows
// them rather than being silently rounded.
export const formatAmount = (minorUnits: number, currency: BillingCurrency) =>
    new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    }).format(minorUnits / 100);

/**
 * How many minor units make one whole unit of a currency -- 100 for Eur and
 * Usd, 1 for Jpy. Read from Intl rather than hardcoded to 100, the
 * `Money/format.ts` reason: a fixed hundred turns a yen price into a
 * hundredth of itself.
 */
const minorUnitsPerWholeUnit = (currency: string): number => {
    const digits =
        new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency.toUpperCase(),
        }).resolvedOptions().maximumFractionDigits ?? 2;

    return 10 ** digits;
};

/**
 * What one month of a plan costs, whichever cadence it is bought on.
 *
 * The screen leads with this number rather than with what Stripe will
 * charge, because the question a customer is actually asking at the plan
 * picker is "which of these is cheaper", and a monthly price against an
 * annual one is not a comparison anybody can do in their head. The amount
 * that will be billed does not disappear -- it sits underneath -- so the
 * two are always on screen together.
 *
 * **Always a whole unit, never cents.** The pricing rule in
 * `infrastructure/core/stripe.tf` strikes an annual price AS a per-month
 * figure -- one month free, floored to a whole unit ending in 4 or 9 --
 * times twelve, so for the current book the division is exact and this
 * floor changes nothing: EUR 1,068 / 12 is EUR 89 on the nose.
 *
 * The floor exists for prices struck under an OLDER rule, which rounded the
 * annual total instead and so does not divide evenly. Those produced
 * "US$97.42 /month", and a figure with cents in it is not a price -- it
 * reads as a conversion artefact, which is exactly what it is. Flooring
 * (never rounding up) keeps it in the customer's favour and matches the
 * direction the rule itself rounds; the exact amount Stripe will charge is
 * on the line underneath either way, so nothing here has to carry it.
 */
export const perMonthMinorUnits = (
    minorUnits: number,
    cadence: Cadence,
    currency: string,
): number => {
    if (cadence !== "yearly") {
        return minorUnits;
    }

    const step = minorUnitsPerWholeUnit(currency);

    return Math.floor(minorUnits / 12 / step) * step;
};

/**
 * What a year on the yearly price saves against paying monthly, in minor
 * units, or undefined when there is nothing to claim.
 *
 * Derived from the two prices the endpoint already sends rather than
 * stated. The screen used to carry a hardcoded "two months off" line that
 * had been wrong for a year -- the prices said about 1.3 months -- and the
 * lesson taken then was that a number which has to be re-derived from
 * Stripe every time the book changes is better left off than left wrong.
 * Deriving it is the third option, and the reason it is available now is
 * that `useGetSelfServePlansQuery` returns BOTH cadences: the monthly price
 * to compare against is already in hand, so this cannot drift from what
 * Stripe will charge.
 *
 * Guarded rather than assumed positive: a book where the annual price is
 * not a discount is a pricing mistake, and the screen's job then is to show
 * the prices and say nothing, not to advertise a negative saving.
 */
export const yearlySavingMinorUnits = (
    monthlyMinorUnits: number | undefined,
    yearlyMinorUnits: number | undefined,
): number | undefined => {
    if (monthlyMinorUnits === undefined || yearlyMinorUnits === undefined) {
        return undefined;
    }

    const saving = monthlyMinorUnits * 12 - yearlyMinorUnits;

    return saving > 0 ? saving : undefined;
};
